import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { it } from "./locales/it";
import { en } from "./locales/en";

/**
 * Language detection priority (in order):
 *   1) ?lang=xx query string  → highest priority, also persisted to localStorage
 *   2) localStorage.cxoora_lang  → previously chosen language
 *   3) Hostname-based fallback  →  cxoora.it → it ; cxoora.eu → en ; default → it
 *
 * IT remains the global default for cxoora.com. We deliberately ignore the
 * browser's "navigator.language" so the experience matches the brand intent.
 */

const SUPPORTED = ["it", "en"];

const detectInitialLanguage = () => {
  try {
    if (typeof window === "undefined") return "it";

    // 1) URL query param ?lang=xx (sticky: written to localStorage)
    const params = new URLSearchParams(window.location.search || "");
    const fromQuery = (params.get("lang") || "").toLowerCase();
    if (SUPPORTED.includes(fromQuery)) {
      try { window.localStorage.setItem("cxoora_lang", fromQuery); } catch (_) { /* localStorage unavailable */ }
      return fromQuery;
    }

    // 2) localStorage (user already picked a language)
    const fromStorage = (window.localStorage.getItem("cxoora_lang") || "").toLowerCase();
    if (SUPPORTED.includes(fromStorage)) return fromStorage;

    // 3) Hostname-based fallback (only if user has never chosen)
    const host = (window.location.hostname || "").toLowerCase();
    if (host.endsWith(".it") || host === "cxoora.it") return "it";
    if (host.endsWith(".eu") || host === "cxoora.eu") return "en";
    // cxoora.com (and any other host like emergentagent.com preview) → IT default
    return "it";
  } catch (_) {
    return "it";
  }
};

const initialLng = detectInitialLanguage();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { it: { translation: it }, en: { translation: en } },
    lng: initialLng,
    fallbackLng: "it",
    supportedLngs: SUPPORTED,
    interpolation: { escapeValue: false },
    detection: {
      // IT is the default; only switch if the user explicitly chose another via
      // the switcher OR provided a ?lang= query param on this visit.
      order: ["localStorage"],
      lookupLocalStorage: "cxoora_lang",
      caches: ["localStorage"],
    },
  });

// Keep i18n + localStorage in sync whenever the user changes language via the switcher.
i18n.on("languageChanged", (lng) => {
  try {
    if (SUPPORTED.includes(lng)) {
      window.localStorage.setItem("cxoora_lang", lng);
      // Update <html lang="…"> for SEO + accessibility
      if (typeof document !== "undefined" && document.documentElement) {
        document.documentElement.setAttribute("lang", lng);
      }
    }
  } catch (_) { /* noop */ }
});

// Make sure <html lang="…"> reflects the initial language at boot.
try {
  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.setAttribute("lang", initialLng);
  }
} catch (_) { /* noop */ }

export default i18n;
